import React, { useState, useEffect, useRef, useCallback } from 'react';
import { HiOutlineSearch, HiX } from 'react-icons/hi';
import classNames from 'classnames';
import { SearchInputProps } from './Search.types';
import { debounce } from '../../utils/debounce';

/**
 * Enhanced SearchInput component that provides a standardized search experience
 * with debouncing, loading state, and clear functionality.
 */
export const SearchInput: React.FC<SearchInputProps> = ({
  value: propValue = '',
  placeholder = 'Search...',
  onChange,
  onSearch,
  onClear,
  loading = false,
  debounce: debounceMs = 300,
  className,
  disabled = false,
  autoFocus = false,
  name = 'search',
  icon,
  showClearButton = true,
  inputProps = {},
}) => {
  // Use internal state if value is not controlled
  const [internalValue, setInternalValue] = useState(propValue);
  const value = propValue || internalValue;
  const inputRef = useRef<HTMLInputElement>(null);

  // Create debounced onChange handler
  const debouncedOnChange = useCallback(
    debounce((value: string) => {
      onChange?.(value);
    }, debounceMs),
    [onChange, debounceMs]
  );

  // Update internal value when prop value changes
  useEffect(() => {
    if (propValue !== undefined) {
      setInternalValue(propValue);
    }
  }, [propValue]);

  // Handle input changes
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setInternalValue(newValue);
    
    // Update parent component immediately for UI responsiveness
    onChange?.(newValue); // Add immediate update - no debounce
    
    // Still use debouncing for filtering operations
    debouncedOnChange(newValue);
  };

  // Handle key press events (Enter)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && onSearch) {
      onSearch(value);
    }
  };

  // Handle clear button click
  const handleClear = () => {
    setInternalValue('');
    // Immediate update for UI responsiveness
    onChange?.('');
    onClear?.();
    
    // Focus the input after clearing
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  return (
    <div className={classNames('relative w-full', className)}>
      <div className="absolute inset-y-0 left-0 flex items-center pl-3 pointer-events-none text-gray-500">
        {icon || <HiOutlineSearch className="w-5 h-5" />}
      </div>

      <input
        ref={inputRef}
        type="text"
        name={name}
        value={value}
        placeholder={placeholder}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        disabled={disabled || loading}
        autoFocus={autoFocus}
        className={classNames(
          'w-full h-12 py-2 pl-10 pr-10 text-md',
          'border border-gray-200 rounded-lg',
          'focus:outline-none focus:ring-2 focus:ring-primary-50 focus:border-primary-100',
          'transition-colors duration-200',
          { 'bg-gray-100 cursor-not-allowed': disabled || loading }
        )}
        {...inputProps}
      />

      {(loading || (showClearButton && value)) && (
        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {loading ? (
            <svg className="animate-spin h-5 w-5 text-gray-400" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 focus:outline-none"
              aria-label="Clear search"
            >
              <HiX className="w-5 h-5" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchInput;